import React, { useEffect, useState } from "react";
import styles from "./Inbox.module.css";
import AxiosInstance, {
  TokenService,
} from "../../../services/Api";
import BlobImage from "../../../components/BlobImage/BlobImage";
import moment from "moment";
import { useSelector } from "react-redux";

const Inbox = () => {
  const user = TokenService.getUser();
  const userId = user?.id || 0;
  const { translations } = useSelector((state) => state.language);

  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchReceivedMessages();
    }
  }, []);

  const fetchReceivedMessages = async () => {
    try {
      const response = await AxiosInstance.post("/message/getMessage", {
        user_id: String(userId),
      });

      if (response.data?.status && Array.isArray(response.data.data)) {
        const formatted = response.data.data.map((msg) => ({
          id: msg.id,
          subject: msg.subject || "",
          body: msg.messages || "",
          senderInitial: msg.subject?.charAt(0)?.toUpperCase() || "M",
          time: moment
            .utc(msg.created_at)
            .utcOffset("+05:30")
            .format("DD MMM YYYY, hh:mm A"),
          attached_file: msg.attached_file || null,
          original_file: msg.original_file || null,
          isRead: msg.read_at === 1 || msg.read_at === "1",
        }));

        setMessages(formatted);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  const handleSelectMessage = async (message) => {
    setSelectedMessageId(message.id);
    setSelectedMessage(message);

    // Mark as read via viewMessage API
    if (!message.isRead) {
      try {
        const response = await AxiosInstance.put("/message/viewMessage", {
          user_id: Number(userId),
          id: message.id,
        });

        if (response.data?.status) {
          // Update local state to mark as read
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === message.id ? { ...msg, isRead: true } : msg,
            ),
          );
          setSelectedMessage((prev) => ({ ...prev, isRead: true }));
        }
      } catch (err) {
        console.error("Error marking message as read:", err);
      }
    }
  };

  return (
    <div className={styles.inboxContainer}>
      {/* LEFT PANEL */}
      <div className={styles.messageList}>
        {messages.length > 0 ? (
          messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.messageItem} ${
                selectedMessageId === message.id ? styles.selected : ""
              } ${!message.isRead ? styles.unread : ""}`}
              onClick={() => handleSelectMessage(message)}
            >
              <div className={styles.messageHeader}>
                <div
                  className={`${styles.avatar} ${
                    !message.isRead ? styles.avatarUnread : ""
                  }`}
                >
                  {message.senderInitial}
                </div>

                <div className={styles.messageInfo}>
                  <div className={styles.messageTitleRow}>
                    <h4
                      className={`${styles.messageTitle} ${
                        !message.isRead ? styles.unreadTitle : ""
                      }`}
                    >
                      {message.subject}
                    </h4>
                    <span className={styles.messageTime}>{message.time}</span>
                  </div>
                  <p className={styles.messageSender}>
                    {message.body?.length > 30
                      ? `${message.body.substring(0, 30)}...`
                      : message.body}
                  </p>

                  {message.attached_file && (
                    <span className={styles.attachmentIcon}>📎</span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>
              {translations.labels?.noMessagesFound ||
                "No messages found"}
            </p>
          </div>
        )}
      </div>

      {/* RIGHT PANEL */}
      <div className={styles.messageDetailPanel}>
        {selectedMessage ? (
          <div className={styles.messageDetail}>
            <div className={styles.messageDetailHeader}>
              <h3 className={styles.messageSubject}>
                {selectedMessage.subject}
              </h3>
              <span className={styles.messageDetailTime}>
                {selectedMessage.time}
              </span>
            </div>

            <div className={styles.messageBody}>
              <p>{selectedMessage.body}</p>
            </div>

            {selectedMessage?.attached_file && (
              <BlobImage
                path={selectedMessage.attached_file}
                fallback="/images/image-not-found.png"
                alt={selectedMessage.original_file || "Attachment"}
                className={styles.attachmentImage}
              />
            )}
          </div>
        ) : (
          <div className={styles.emptyMessageDetail}>
            <p>
              {translations.labels?.selectMessageToViewDetails ||
                "Select a message to view details"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inbox;